<template>
  <div class="production-container flex h-full w-full flex-col overflow-hidden bg-[#121212]" data-test="production-view">
    <LoadingScreen :visible="!loaded" />

    <div v-if="showErrorState" class="centered-content flex h-full items-center justify-center">
      <div class="error-state flex items-start gap-3">
        <i class="fas fa-exclamation-triangle fa-4x"></i>

        <div class="error-state-body">
          <h1>Uh oh</h1>

          <p>We ran into an issue while attempting to load this Production.</p>
        </div>
      </div>
    </div>

    <div
      v-if="!showErrorState && loaded"
      class="view-production flex-grow"
      ref="producerCanvas"
    >
        <splitter
          v-model="horizontalSizes"
          class="h-full"
        >
        <splitter-panel
          :fixed-width="true"
          :default-width="300"
          :min-width="assetLibraryMinWidth"
          :collapse-below="assetLibraryCollapseWidth"
          :collapse-buffer="100"
          data-test="producer-panel-assetLibrary"
        >
          <resource-library
            :collapsed="isAssetLibraryCollapsed"
            ref="assetLibrary"
            @expand="setAssetLibraryWidth"
          />
        </splitter-panel>

        <splitter-panel>
          <splitter
            v-model="verticalLeftSizes"
            vertical
            class="production-content__column h-full min-w-0"
          >
            <splitter-panel :min-height="40">
              <draggable
                v-model="panel1"
                data-panel="1"
                v-bind="draggablePanelProps"
                v-on="draggablePanelListeners"
                data-test="producer-panel-1"
              >
                <keep-alive>
                  <component
                    :is="getProducerPanel(panel1[0]).component"
                    v-bind="getProducerPanel(panel1[0]).props"
                    :key="`panel1-${panel1[0]}`"
                  />
                </keep-alive>
              </draggable>
            </splitter-panel>

            <splitter-panel :min-height="40">
              <draggable
                v-model="panel3"
                data-panel="3"
                v-bind="draggablePanelProps"
                v-on="draggablePanelListeners"
                data-test="producer-panel-3"
              >
                <keep-alive>
                  <component
                    :is="getProducerPanel(panel3[0]).component"
                    v-bind="getProducerPanel(panel3[0]).props"
                    :key="`panel3-${panel3[0]}`"
                  />
                </keep-alive>
              </draggable>
            </splitter-panel>
          </splitter>
        </splitter-panel>

        <splitter-panel>
          <splitter
            v-model="verticalRightSizes"
            vertical
            class="production-content__column h-full min-w-0"
          >
            <splitter-panel :min-height="40">
              <draggable
                v-model="panel2"
                data-panel="2"
                v-bind="draggablePanelProps"
                v-on="draggablePanelListeners"
                data-test="producer-panel-2"
              >
                <keep-alive>
                  <component
                    :is="getProducerPanel(panel2[0]).component"
                    v-bind="getProducerPanel(panel2[0]).props"
                    :key="`panel2-${panel2[0]}`"
                  />
                </keep-alive>
              </draggable>
            </splitter-panel>

            <splitter-panel :min-height="40">
              <draggable
                v-model="panel4"
                data-panel="4"
                v-bind="draggablePanelProps"
                v-on="draggablePanelListeners"
                data-test="producer-panel-4"
              >
                <keep-alive>
                  <component
                    :is="getProducerPanel(panel4[0]).component"
                    v-bind="getProducerPanel(panel4[0]).props"
                    :key="`panel4-${panel4[0]}`"
                  />
                </keep-alive>
              </draggable>
            </splitter-panel>
          </splitter>
        </splitter-panel>
      </splitter>

      <post-modal />

      <update-production-modal
        v-if="showUpdateProductionModal"
        data-test="producer-updateProduction-modal"
        @close="handleCloseUpdateProductionModal"
      />

      <screen-modal
        v-if="showManageScreensModal"
        data-test="producer-manageOutputs-modal"
        @close="handleCloseManageScreensModal"
      />

      <shoppable-modal
        :value="showCatalogModal"
        data-test="producer-catalog-modal"
        @close="closeCatalogModal"
      />
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';

import LayerEditor from '@/js/components/layer-editor/LayerEditor.vue';
import LivePreview from '@/js/components/LivePreview.vue';
import LoadingScreen from '@/js/components/LoadingScreen.vue';
import PostModal from '@/js/components/PostModal.vue';
import ResourceLibrary from '@/js/routes/resource-library/ResourceLibrary.vue';
import ScreenModal from '@/js/components/connect-screen/ScreenModal.vue';
import Timeline from '@/js/components/timeline/Timeline.vue';
import UpdateProductionModal from '@/js/components/update-production/UpdateProduction.vue';
import ShoppableModal from '@/js/components/modal/ShoppableModal.vue';
import Splitter from '@/js/components/splitter/Splitter.vue';
import SplitterPanel from '@/js/components/splitter/SplitterPanel.vue';

import toast from '@/js/components/toast/toast';

export default {
  name: 'ProductionComponent',

  components: {
    LayerEditor,
    LivePreview,
    LoadingScreen,
    PostModal,
    ResourceLibrary,
    ScreenModal,
    Timeline,
    UpdateProductionModal,
    Splitter,
    SplitterPanel,
    ShoppableModal,
  },

  data() {
    return {
      showErrorState: false,
      loaded: false,
      editing: false,
      assetLibraryWidth: 300,
      assetLibraryCollapseWidth: 285,
      assetLibraryMinWidth: 40,
      preservedAssetLibraryWidth: null,
      assetLibraryObserver: null,
      previewTimelineWidth: 40,
      liveLayerEditorWidth: 40,
      previewHeight: 50,
      timelineHeight: 50,
      liveHeight: 50,
      layerEditorHeight: 50,
      panel1: ['preview'],
      panel2: ['live'],
      panel3: ['timeline'],
      panel4: ['layer-editor'],
      panelsToSwap: {
        from: null,
        to: null,
      },
      draggablePanelProps: {
        class: "h-full w-full overflow-hidden",
        group: {
          name: 'producerPanel',
          pull: true,
          put: ['producerPanel'],
        },
        sort: false,
        handle: '.panel-drag-handle',
        filter: '.panel-drag-exclude',
        preventOnFilter: false,
        move: this.moveProducerPanel,
      },
      draggablePanelListeners: {
        remove: this.swapProducerPanels,
        end: this.endMoveProducerPanel,
      },
    };
  },

  created() {
    this.load();
  },

  mounted() {
    if (localStorage.assetLibraryWidth) {
      this.assetLibraryWidth = JSON.parse(localStorage.assetLibraryWidth);
      // patch for old data
      if (this.assetLibraryWidth < this.assetLibraryCollapseWidth) {
        this.assetLibraryWidth = this.assetLibraryCollapseWidth;
      }
    }

    if (localStorage.previewTimelineWidth) this.previewTimelineWidth = JSON.parse(localStorage.previewTimelineWidth);
    if (localStorage.liveLayerEditorWidth) this.liveLayerEditorWidth = JSON.parse(localStorage.liveLayerEditorWidth);
    if (localStorage.previewHeight) this.previewHeight = JSON.parse(localStorage.previewHeight);
    if (localStorage.timelineHeight) this.timelineHeight = JSON.parse(localStorage.timelineHeight);
    if (localStorage.liveHeight) this.liveHeight = JSON.parse(localStorage.liveHeight);
    if (localStorage.layerEditorHeight) this.layerEditorHeight = JSON.parse(localStorage.layerEditorHeight);
    if (localStorage.producerPanel1) this.panel1 = JSON.parse(localStorage.producerPanel1);
    if (localStorage.producerPanel2) this.panel2 = JSON.parse(localStorage.producerPanel2);
    if (localStorage.producerPanel3) this.panel3 = JSON.parse(localStorage.producerPanel3);
    if (localStorage.producerPanel4) this.panel4 = JSON.parse(localStorage.producerPanel4);
  },

  beforeUnmount() {
    this.assetLibraryObserver?.unobserve(this.$refs.producerCanvas);
  },

  watch: {
    'production.title': {
      immediate: true,
      handler(title) {
        if (title) {
          document.querySelector('title').innerText = `${title} - Tagboard Producer`;
        }
      },
    },

    loaded(v) {
      if (v) {
        // Do this here instead of mounted because the ref we want
        // to observe is v-if'd with loaded so it won't be available there
        this.$nextTick(() => {
          this.assetLibraryObserver = new ResizeObserver(this.handleProducerCanvasResize);
          this.assetLibraryObserver.observe(this.$refs.producerCanvas);
        });
      }
    },
  },

  methods: {
    ...mapActions('shoppable', {
      getShoppableSettings: 'getSettingsByAffiliate',
    }),
    ...mapActions([
      'getProduction',
      'clearProduction',
      'setOwner',
      'getUserAccounts',
      'getPermissions',
      'getGraphics',
      'getTimelineAssets',
      'generateShortUrls',
      'trackSportradarInGameElement',
    ]),
    ...mapActions('shoppable', [
      'closeCatalogModal',
    ]),

    /**
    * Load EVERYTHING.
    * This is pretty annoying, and needs to be broken up into junks that can be retried individually.
    * Otherwise, if ANYTHING fails to load, the user will see a broken production screen.
    */
    async load() {
      const productionId = +this.$route.params.production_id;

      // Do initial load of production
      const production = await this.getProduction(productionId).catch((err) => {
        console.error('Failed to load production:', err);

        toast.makeToast({
          heading: 'Error',
          icon: 'error',
          loaderBg: 'red',
          text: 'Failed to load production.',
        });

        this.showErrorState = true;
      });

      if (!production) { return; }

      // Make sure user has access to the production. It's possible to load it without permissions,
      // but nothing else in the editor will work if the user is not on the team.
      if (this.$store.state.user.id !== production.owner) {
        const accounts = await this.getUserAccounts();
        const ownerIds = Object.keys(accounts.owners).map(Number);

        if (!ownerIds.includes(production.owner)) {
          // If user doesn't have access, we'll send them back to the producer dashboard.
          // After we clear state and make sure `owner` is reset.
          this.setOwner(null);
          this.clearProduction();
          this.$router.replace('/');
          return;
        }
      }

      // Join production socket room to listen for updates
      $socket.emit('production', { production_id: productionId });

      // Load additional info needed to show/hide tabs, saved graphics, etc.
      await Promise.all([
        // Get permissions, so we know if we should show the smart panels tab
        this.getPermissions(),

        // Saved Graphics (not normal graphic templates)
        this.getGraphics().catch((err) => {
          // Just log out error w/o a toast.
          // The user can use the refresh button to reload saved graphics anyway.
          console.error('Failed to load saved graphics:', err);
        }),

        // Load assets currently in the timeline
        // so they are added to their respective lookup tables
        this.getTimelineAssets(),

        // Load shoppable settings
        this.getShoppableSettings(),
      ]);

      this.loaded = true;

      // Backfill any missing short URLs for QR codes in the timeline
      this.generateShortUrls();
      this.trackSportradarInGameElement();
    },

    cancel() {
      this.editing = false;
      this.title = this.production.title;
    },

    done() {
      this.editing = false;
    },

    handleCloseUpdateProductionModal() {
      this.$store.dispatch('toggleUpdateProductionModal');
    },

    handleCloseManageScreensModal() {
      this.$store.dispatch('toggleScreensModal');
    },

    handleCloseHighlightsModal() {
      this.$store.dispatch('toggleHighlightsModal');
    },

    moveProducerPanel(e) {
      this.panelsToSwap.from = e.from.dataset.panel;
      this.panelsToSwap.to = e.to.dataset.panel;
      return -1; // control the order it's inserted at for easier swapping
    },

    swapProducerPanels() {
      if (this.panelsToSwap.from && this.panelsToSwap.to) {
        const { from, to } = this.panelsToSwap;

        // The data is updated already so we just need to complete the swap
        const toPanel = this[`panel${to}`];

        this[`panel${from}`] = [toPanel[1]];
        this[`panel${to}`] = [toPanel[0]];

        localStorage[`producerPanel${from}`] = JSON.stringify([toPanel[1]]);
        localStorage[`producerPanel${to}`] = JSON.stringify([toPanel[0]]);
      }
    },

    endMoveProducerPanel(e) {
      this.panelsToSwap = {
        from: null,
        to: null,
      };
    },

    getProducerPanel(panel) {
      switch (panel) {
        case 'preview':
          return {
            component: 'live-preview',
            props: {
              output: 'preview',
              iframeWidth: this.iframeWidth,
              iframeHeight: this.iframeHeight,
            },
          };
        case 'live':
          return {
            component: 'live-preview',
            props: {
              output: 'live',
              iframeWidth: this.iframeWidth,
              iframeHeight: this.iframeHeight,
            },
          };
        case 'timeline':
          return {
            component: 'timeline',
          };
        case 'layer-editor':
          return {
            component: 'layer-editor',
          };

        default:
          return {
            component: 'timeline',
          };
      }
    },

    setAssetLibraryWidth(targetWidth = this.assetLibraryCollapseWidth) {
      if (!this.$refs.producerCanvas) {
        return;
      }

      // convert collapse width to percentage
      const assetLibraryWidthPct = +parseFloat((targetWidth / this.$refs.producerCanvas.clientWidth) * 100).toFixed(4);

      let newPreviewTimelineWidth = this.previewTimelineWidth - assetLibraryWidthPct;
      let newLiveLayerEditorWidth = this.liveLayerEditorWidth;

      // keep the other panels at 0 if they are already at ~0
      if (this.previewTimelineWidth < 1) {
        newLiveLayerEditorWidth = (100 - assetLibraryWidthPct);
      } else if (this.liveLayerEditorWidth < 1) {
        newPreviewTimelineWidth = (100 - assetLibraryWidthPct);
      } else if (this.previewTimelineWidth <= assetLibraryWidthPct && this.liveLayerEditorWidth <= assetLibraryWidthPct) {
        newPreviewTimelineWidth = this.previewTimelineWidth;
        newLiveLayerEditorWidth = this.liveLayerEditorWidth;
      } else if (this.previewTimelineWidth <= assetLibraryWidthPct) {
        newPreviewTimelineWidth = this.previewTimelineWidth;
        newLiveLayerEditorWidth -= assetLibraryWidthPct;
      }

      this.horizontalSizes = [targetWidth, newPreviewTimelineWidth, newLiveLayerEditorWidth];
    },

    handleProducerCanvasResize(entries) {
      const { width } = entries[0].contentRect;

      if (this.assetLibraryWidth > width) {
        this.setAssetLibraryWidth(width - 20);
      }
    },
  },

  computed: {
    ...mapState({
      production: (state) => state.production.production,
      iframeWidth: (state) => state.production.production.xDimension,
      iframeHeight: (state) => state.production.production.yDimension,
    }),

    ...mapState('shoppable', [
      'showCatalogModal',
    ]),

    hasPanels() {
      return this.$store.state.user.hasPanels;
    },

    production() {
      return this.$store.state.production.production;
    },

    showUpdateProductionModal() {
      return this.$store.state.clients.showUpdateProductionModal;
    },

    showManageScreensModal() {
      return this.$store.state.clients.showScreensModal;
    },

    showWHighlightsModal() {
      return this.$store.state.clients.showHighlightsModal;
    },

    isAssetLibraryCollapsed() {
      return this.assetLibraryWidth <= this.assetLibraryMinWidth;
    },

    horizontalSizes: {
      get() {
        return [
          this.assetLibraryWidth,
          this.previewTimelineWidth,
          this.liveLayerEditorWidth,
        ];
      },
      set(v) {
        [this.assetLibraryWidth, this.previewTimelineWidth, this.liveLayerEditorWidth] = v;
        localStorage.assetLibraryWidth = JSON.stringify(this.assetLibraryWidth);
        localStorage.previewTimelineWidth = JSON.stringify(this.previewTimelineWidth);
        localStorage.liveLayerEditorWidth = JSON.stringify(this.liveLayerEditorWidth);
      },
    },
    verticalLeftSizes: {
      get() {
        return [this.previewHeight, this.timelineHeight];
      },
      set(v) {
        [this.previewHeight, this.timelineHeight] = v;
        localStorage.previewHeight = JSON.stringify(this.previewHeight);
        localStorage.timelineHeight = JSON.stringify(this.timelineHeight);
      },
    },
    verticalRightSizes: {
      get() {
        return [this.liveHeight, this.layerEditorHeight];
      },
      set(v) {
        [this.liveHeight, this.layerEditorHeight] = v;
        localStorage.liveHeight = JSON.stringify(this.liveHeight);
        localStorage.layerEditorHeight = JSON.stringify(this.layerEditorHeight);
      },
    },
  },
};
</script>
